// Notification + chat sounds for desktop & mobile web.
// Generates real WAV audio (data URIs) and plays them through an <audio> element at
// full volume — far louder and more reliable across devices than synthesized Web Audio.
// A one-time "unlock" on the first user gesture satisfies mobile autoplay rules.

const PREF_KEY = "sh_sound_on";

export function isSoundOn() {
  try { const v = localStorage.getItem(PREF_KEY); return v === null ? true : v === "1"; }
  catch { return true; }
}
export function setSoundOn(on) {
  try { localStorage.setItem(PREF_KEY, on ? "1" : "0"); } catch {}
  if (on) primeAudio();
}

// ——— Sound definitions: one distinct tone-sequence per user ———
export const NOTIF_SOUNDS = [
  { name: "Ding-dong",    notes: [{ f: 880, d: 0.20 }, { f: 1174.7, at: 0.13, d: 0.40 }] },
  { name: "Rising triad", notes: [{ f: 659.25, d: 0.14 }, { f: 830.6, at: 0.12, d: 0.14 }, { f: 1046.5, at: 0.24, d: 0.34 }] },
  { name: "Soft bell",    notes: [{ f: 523.25, d: 0.5, t: "triangle" }, { f: 783.99, at: 0.0, d: 0.5, t: "triangle", g: 0.4 }] },
  { name: "Chirp up",     notes: [{ f: 1046.5, d: 0.22, ft: 1760 }] },
  { name: "Descending",   notes: [{ f: 1174.7, d: 0.18 }, { f: 880, at: 0.15, d: 0.34 }] },
  { name: "Marimba",      notes: [{ f: 587.3, d: 0.13, t: "square", g: 0.4 }, { f: 880, at: 0.12, d: 0.13, t: "square", g: 0.4 }, { f: 1174.7, at: 0.24, d: 0.26, t: "square", g: 0.4 }] },
  { name: "Warm chord",   notes: [{ f: 523.25, d: 0.55, g: 0.34 }, { f: 659.25, d: 0.55, g: 0.34 }, { f: 783.99, d: 0.55, g: 0.34 }] },
  { name: "Double beep",  notes: [{ f: 988, d: 0.13 }, { f: 988, at: 0.18, d: 0.18 }] },
];
const CHAT_NOTES = [{ f: 540, d: 0.16, ft: 800 }];

export function notifVariantFor(id) {
  const s = String(id || ""); let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % NOTIF_SOUNDS.length;
}

// ——— WAV synthesis (16-bit PCM mono → base64 data URI) ———
function buildWav(notes) {
  const sr = 22050;
  let total = 0.1;
  notes.forEach(n => { total = Math.max(total, (n.at || 0) + (n.d || 0.25)); });
  total += 0.1;
  const N = Math.ceil(total * sr);
  const buf = new Float32Array(N);
  notes.forEach(n => {
    const start = Math.floor((n.at || 0) * sr);
    const len = Math.floor((n.d || 0.25) * sr);
    const g = n.g != null ? n.g : 0.7;
    for (let i = 0; i < len; i++) {
      const t = i / sr;
      const env = Math.min(1, t / 0.008) * Math.exp(-t * 3.0);
      const freq = n.ft ? (n.f + (n.ft - n.f) * (i / len)) : n.f;
      let s = Math.sin(2 * Math.PI * freq * t);
      if (n.t === "square") s = s >= 0 ? 1 : -1;
      else if (n.t === "triangle") s = Math.asin(Math.sin(2 * Math.PI * freq * t)) * (2 / Math.PI);
      const idx = start + i;
      if (idx < N) buf[idx] += s * env * g;
    }
  });
  let peak = 0; for (let i = 0; i < N; i++) peak = Math.max(peak, Math.abs(buf[i]));
  const norm = peak > 1 ? 0.97 / peak : 1;
  const dv = new DataView(new ArrayBuffer(44 + N * 2));
  let p = 0;
  const ws = (s) => { for (let i = 0; i < s.length; i++) dv.setUint8(p++, s.charCodeAt(i)); };
  ws("RIFF"); dv.setUint32(p, 36 + N * 2, true); p += 4; ws("WAVE"); ws("fmt ");
  dv.setUint32(p, 16, true); p += 4; dv.setUint16(p, 1, true); p += 2; dv.setUint16(p, 1, true); p += 2;
  dv.setUint32(p, sr, true); p += 4; dv.setUint32(p, sr * 2, true); p += 4; dv.setUint16(p, 2, true); p += 2; dv.setUint16(p, 16, true); p += 2;
  ws("data"); dv.setUint32(p, N * 2, true); p += 4;
  for (let i = 0; i < N; i++) { let v = Math.max(-1, Math.min(1, buf[i] * norm)); dv.setInt16(p, v * 32767, true); p += 2; }
  const u8 = new Uint8Array(dv.buffer);
  let bin = ""; const CH = 0x8000;
  for (let i = 0; i < u8.length; i += CH) bin += String.fromCharCode.apply(null, u8.subarray(i, i + CH));
  return "data:audio/wav;base64," + btoa(bin);
}

const wavCache = {};
function wavForVariant(idx) { if (!wavCache[idx]) wavCache[idx] = buildWav(NOTIF_SOUNDS[idx].notes); return wavCache[idx]; }
let chatWav = null;
function chatWavUri() { if (!chatWav) chatWav = buildWav(CHAT_NOTES); return chatWav; }

// Dedicated <audio> elements (reused) so playback is instant and reliable.
let notifEl = null, chatEl = null, primed = false, installed = false;
function notifAudio() { if (!notifEl) { notifEl = new Audio(); notifEl.preload = "auto"; } return notifEl; }
function chatAudio() { if (!chatEl) { chatEl = new Audio(); chatEl.preload = "auto"; } return chatEl; }

function play(el, src) {
  if (!isSoundOn()) return;
  try {
    el.src = src; el.volume = 1.0; el.currentTime = 0;
    const pr = el.play();
    if (pr && pr.catch) pr.catch(() => {});
  } catch {}
}

export function playNotificationVariant(idx) {
  const n = (((Number(idx) || 0) % NOTIF_SOUNDS.length) + NOTIF_SOUNDS.length) % NOTIF_SOUNDS.length;
  play(notifAudio(), wavForVariant(n));
}
export function playNotification() { playNotificationVariant(0); }
export function playChat() { play(chatAudio(), chatWavUri()); }

// Prime audio within a user gesture so mobile browsers allow later playback.
function primeAudio() {
  if (primed) return;
  primed = true;
  try {
    const a = notifAudio();
    a.src = wavForVariant(0); a.muted = true;
    const pr = a.play();
    if (pr && pr.then) pr.then(() => { a.pause(); a.currentTime = 0; a.muted = false; }).catch(() => { a.muted = false; primed = false; });
    else { a.pause(); a.muted = false; }
  } catch { primed = false; }
}

export function unlockAudio() { primeAudio(); }

export function installAudioUnlock() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const h = () => primeAudio();
  ["pointerdown", "touchstart", "mousedown", "keydown", "click"].forEach(ev =>
    window.addEventListener(ev, h, { passive: true })
  );
}
